import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { ICrpPeriodFigures } from '../../../../shared/services/crp-report/crp-report.model'

const fmt = (value: number) => Number(value ?? 0).toLocaleString('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/**
 * The Annex A emissions table for one year. Scope 3 is broken out per category so a reader can see
 * which required sources are included — a single "Scope 3 (Included Sources)" figure would hide a
 * missing category behind one number.
 */
export const CrpEmissionsTable: FunctionComponent<{ period: ICrpPeriodFigures | null }> = ({ period }) => {
  if (!period) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        No emissions data available for this period.
      </Typography>
    )
  }

  const valueCell = (value: number) => (
    <TableCell align='right'>{fmt(value)}</TableCell>
  )

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size='small' sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            <TableCell>Emissions</TableCell>
            <TableCell align='right'>Total (tCO2e)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Scope 1</TableCell>
            {valueCell(period.scopeOne)}
          </TableRow>
          <TableRow>
            <TableCell>Scope 2 (location-based)</TableCell>
            {valueCell(period.scopeTwoLocation)}
          </TableRow>
          {period.scopeTwoMarket > 0 && (
            <TableRow>
              <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Scope 2 (market-based), supplementary
              </TableCell>
              <TableCell align='right' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                {fmt(period.scopeTwoMarket)}
              </TableCell>
            </TableRow>
          )}
          {period.scope3.map((category) => (
            <TableRow key={category.number}>
              <TableCell>
                Scope 3.{category.number}: {category.label}
                {!category.required && (
                  <Typography component='span' variant='caption' sx={{ color: 'text.secondary', ml: 0.5 }}>
                    (beyond PPN 006 minimum)
                  </Typography>
                )}
              </TableCell>
              <TableCell align='right'>
                {category.status === 'NOT_REPORTED'
                  ? <Chip size='small' color='warning' variant='outlined' label='Not reported' />
                  : fmt(category.tco2e)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell>Scope 3: other categories reported</TableCell>
            {valueCell(period.otherScope3Tco2e)}
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontFamily: 'EffraMd' }}>Scope 3 (Included Sources)</TableCell>
            <TableCell align='right' sx={{ fontFamily: 'EffraMd' }}>{fmt(period.scopeThreeTotal)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontFamily: 'EffraMd', borderTop: 2, borderColor: 'primary.main' }}>
              Total Emissions
            </TableCell>
            <TableCell align='right' sx={{ fontFamily: 'EffraMd', borderTop: 2, borderColor: 'primary.main' }}>
              {fmt(period.totalLocation)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
        Total Emissions is Scope 1 + Scope 2 (location-based) + Scope 3 (Included Sources), in line with SECR.
        Any market-based figure is supplementary and is not included in the total.
      </Typography>
    </Box>
  )
}
